import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Sparkles, ArrowRight, CheckCircle2, Flame, Trophy } from "lucide-react";
import { toast } from "sonner";
import { apiErrorToast } from "../lib/utils";

const HERO_IMAGE = "https://images.unsplash.com/photo-1531545514256-b1400bc00f31?auto=format&fit=crop&w=1200&q=75";

export default function Login() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const inviteToken = params.get("invite") || "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      // If an invite token was passed, try to accept it for this user.
      if (inviteToken) {
        try {
          const { data } = await api.post(`/org/invite/${inviteToken}/accept`);
          await refresh?.();
          toast.success(`You've joined ${data?.organization?.name || "the organization"}.`);
          navigate("/app/dashboard", { replace: true });
          return;
        } catch (acceptErr) {
          apiErrorToast(acceptErr, "Signed in, but joining the organization failed.");
        }
      }
      const to = location.state?.from?.pathname || "/app/dashboard";
      toast.success("Welcome back!");
      navigate(to, { replace: true });
    } catch (err) {
      apiErrorToast(err, "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-aurora text-zinc-900">
      {/* LEFT VISUAL */}
      <div className="hidden lg:flex flex-col relative overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Focused candidate preparing for interview"
          className="absolute inset-0 h-full w-full object-cover"
          data-testid="login-hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-700/85 via-sky-600/75 to-emerald-500/75 mix-blend-multiply" />
        <div className="relative z-10 flex flex-col p-12 h-full text-white">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-white/15 backdrop-blur rounded-md flex items-center justify-center ring-1 ring-white/30"><Sparkles size={16} /></div>
            <span className="font-display font-black text-lg">SkillGap <span className="opacity-80">Pilot</span></span>
          </Link>
          <div className="mt-auto">
            <div className="tag" style={{ background: 'rgba(255,255,255,.14)', color: '#fff', borderColor: 'rgba(255,255,255,.22)' }}>Welcome back</div>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-6 leading-[1.02]">
              Pick up where<br />you left off.
            </h1>
            <p className="text-white/85 mt-4 max-w-md">
              Continue your interview prep, track your readiness, and keep your streak alive.
            </p>
            <div className="mt-8 flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2"><Flame size={14} className="text-amber-300" /> 12-day streaks live</div>
              <div className="flex items-center gap-2"><Trophy size={14} className="text-amber-300" /> 7 badges to unlock</div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT FORM */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-8 w-8 bg-gradient-to-br from-indigo-500 to-sky-500 text-white rounded-md flex items-center justify-center"><Sparkles size={16} /></div>
            <span className="font-display font-black text-lg">SkillGap Pilot</span>
          </div>
          <div className="tag">Login</div>
          <h2 className="font-display font-black text-3xl tracking-tight mt-3">Sign in to your <span className="rainbow-text">account</span></h2>
          <div className="mt-8 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</label>
              <input data-testid="login-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Password</label>
              <input data-testid="login-password" required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full h-11 px-3 border border-zinc-300 rounded-md focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100" />
            </div>
            <button data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-md btn-gradient font-semibold hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Signing in…" : <>Sign in <ArrowRight size={14} /></>}
            </button>
            <p className="text-sm text-zinc-600 text-center">
              New here? <Link to={inviteToken ? `/signup?invite=${encodeURIComponent(inviteToken)}` : "/signup"} className="font-semibold text-indigo-600" data-testid="login-go-signup">Create account</Link>
            </p>
            <div className="pt-3 flex items-center justify-center gap-1.5 text-[11px] text-zinc-500">
              <CheckCircle2 size={12} className="text-emerald-500" /> Secure JWT auth · Cancel anytime
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
